import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type RefObject,
} from 'react';

import { useClickAway } from '@/hooks/useClickAway';
import { classList } from '@/utils/cn';

import { type EditorFeatureSet } from './BaseEditor';
import { EditorPreview } from './EditorPreview';
import { MarkdownEditor } from './MarkdownEditor';

interface SharedMarkdownEditorSession {
  id: string;
  host: HTMLElement;
  content: string;
  version: number | string;
  focusOffset: { x: number; y: number } | null;
  onChange: (markdown: string) => void;
  onAskSelection?: (selection: { text: string }) => void;
  ariaLabel?: string;
  placeholder?: string;
  featureSet?: EditorFeatureSet;
}

let sharedMarkdownEditorSession: SharedMarkdownEditorSession | null = null;
let sharedMarkdownEditorMountCount = 0;
const sharedMarkdownEditorSessionListeners = new Set<() => void>();

function emitSharedMarkdownEditorSessionChange() {
  sharedMarkdownEditorSessionListeners.forEach((listener) => listener());
}

function subscribeSharedMarkdownEditorSession(listener: () => void) {
  sharedMarkdownEditorSessionListeners.add(listener);
  return () => sharedMarkdownEditorSessionListeners.delete(listener);
}

function getSharedMarkdownEditorSessionSnapshot() {
  return sharedMarkdownEditorSession;
}

function setSharedMarkdownEditorSession(
  session: SharedMarkdownEditorSession | null
) {
  sharedMarkdownEditorSession = session;
  emitSharedMarkdownEditorSessionChange();
}

function clearSharedMarkdownEditorSession(id: string) {
  if (sharedMarkdownEditorSession?.id !== id) return;
  setSharedMarkdownEditorSession(null);
}

// Single-active lock: opening one field closes the previously-active one (also
// covers keyboard switching, which the pointer-only click-away misses).
let activeSharedMarkdownEditor: { id: string; close: () => void } | null = null;

/** True while the pooled `SharedMarkdownEditorSingleton` is mounted. */
export function isSharedMarkdownEditorMounted() {
  return sharedMarkdownEditorMountCount > 0;
}

/**
 * The single pooled markdown editor, mounted once at the app root. It parks
 * hidden until a `SharedMarkdownEditor` reparents it into a host, so surfaces
 * share one Tiptap instance instead of each mounting its own.
 */
export function SharedMarkdownEditorSingleton() {
  const session = useSyncExternalStore(
    subscribeSharedMarkdownEditorSession,
    getSharedMarkdownEditorSessionSnapshot,
    getSharedMarkdownEditorSessionSnapshot
  );
  const parkingRef = useRef<HTMLDivElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const sessionRef = useRef(session);
  sessionRef.current = session;

  useEffect(() => {
    sharedMarkdownEditorMountCount += 1;
    return () => {
      sharedMarkdownEditorMountCount -= 1;
    };
  }, []);

  useLayoutEffect(() => {
    const shell = shellRef.current;
    const parking = parkingRef.current;
    if (!shell || !parking) return;

    const target = session?.host ?? parking;
    target.appendChild(shell);
    shell.hidden = !session;
    return () => {
      if (shell.parentNode !== parking) parking.appendChild(shell);
    };
  }, [session]);

  return (
    <div hidden ref={parkingRef}>
      <div
        className="flex min-h-0 flex-1 flex-col"
        ref={shellRef}
        hidden={!session}
      >
        <MarkdownEditor
          id="shared-markdown-editor-singleton"
          initialContent=""
          ariaLabel={session?.ariaLabel}
          placeholder={session?.placeholder}
          featureSet={session?.featureSet}
          extensionMode="full"
          excludeExtensions={['cursorRestore']}
          autofocus={false}
          findScrollableParent={false}
          className="min-h-0 flex-1 lb-editor-reset px-5 pb-2 pt-3"
          syncedContent={session?.content ?? ''}
          syncedContentVersion={
            session ? `${session.id}:${session.version}` : undefined
          }
          focusOnSyncedContent={Boolean(session)}
          syncedFocusOffset={session?.focusOffset ?? null}
          onUpdate={(markdown) => sessionRef.current?.onChange(markdown)}
          onAskSelection={(selection) =>
            sessionRef.current?.onAskSelection?.(selection)
          }
        />
      </div>
    </div>
  );
}

export interface SharedMarkdownEditorProps {
  /** Current markdown value. */
  value: string;
  /** Fired with the edited markdown on every change. */
  onChange: (markdown: string) => void;
  onPreviewChange?: (markdown: string) => void;
  /** When false, the field is a read-only preview with no click-to-edit. */
  editable?: boolean;
  /** Open straight into the focused editor on mount instead of the preview. */
  autoFocus?: boolean;
  /** Click-away boundary; defaults to the field's own wrapper. */
  boundaryRef?: RefObject<HTMLElement | null>;
  /**
   * Bump to re-seed the live editor from `value` after an *external* change
   * (e.g. reset / upstream reconciliation) while the field is being edited.
   */
  syncVersion?: number | string;
  /** Fired when editing stops (click-away, `editable` off, or unmount mid-edit). */
  onEditEnd?: () => void;
  /** Handler for the editor's "ask Littlebird about this selection" action. */
  onAskSelection?: (selection: { text: string }) => void;
  /** Shown in place of the preview when the value is empty. */
  placeholder?: string;
  /** Classes for the field wrapper (both preview and editor states). */
  className?: string;
  /** Accessible label for the field; also drives the "Edit {label}" preview label. */
  ariaLabel?: string;
  /** Which editor feature set to expose. */
  featureSet?: EditorFeatureSet;
}

/**
 * A markdown field that shows a preview until clicked, then borrows the one
 * shared editor for editing and returns to the preview on click-away.
 */
export function SharedMarkdownEditor({
  value,
  onChange,
  onPreviewChange,
  editable = true,
  autoFocus = false,
  boundaryRef,
  syncVersion,
  onEditEnd,
  onAskSelection,
  placeholder,
  className,
  ariaLabel,
  featureSet,
}: SharedMarkdownEditorProps) {
  const editorId = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const focusOffsetRef = useRef<{ x: number; y: number } | null>(null);
  const versionRef = useRef(0);
  const [editing, setEditing] = useState(autoFocus);
  const onEditEndRef = useRef(onEditEnd);
  onEditEndRef.current = onEditEnd;
  const contentRef = useRef(value);
  contentRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onAskSelectionRef = useRef(onAskSelection);
  onAskSelectionRef.current = onAskSelection;

  const active = editable && editing;
  // The pool is mounted at the app root, so this is normally true; the local
  // editor below is only a fallback for the rare case it isn't.
  const useSingleton = active && isSharedMarkdownEditorMounted();

  // Borrow the shared pooled editor into our host while editing: seed it with the
  // current value and stream edits back through `onChange`. `syncVersion` bumps
  // re-seed the open editor after an external `value` change.
  const claimVersion = `${editorId}:${versionRef.current}:${syncVersion ?? ''}`;
  useLayoutEffect(() => {
    if (!useSingleton || !hostRef.current) return;
    setSharedMarkdownEditorSession({
      id: editorId,
      host: hostRef.current,
      content: contentRef.current,
      version: claimVersion,
      focusOffset: focusOffsetRef.current,
      ariaLabel,
      placeholder,
      featureSet,
      onChange: (markdown) => onChangeRef.current(markdown),
      onAskSelection: onAskSelectionRef.current
        ? (selection) => onAskSelectionRef.current?.(selection)
        : undefined,
    });
    return () => clearSharedMarkdownEditorSession(editorId);
    // Callbacks/content read via refs; only re-seed on a version bump or edit toggle.
  }, [
    editorId,
    claimVersion,
    useSingleton,
    ariaLabel,
    placeholder,
    featureSet,
  ]);

  useClickAway(boundaryRef ?? wrapperRef, () => setEditing(false), {
    enabled: active,
    // The bubble menu and its popovers are portaled outside this wrapper;
    // clicking them shouldn't count as clicking away.
    ignoreSelector: [
      '[data-editor-bubble-menu]',
      '[data-editor-floating-ui]',
      '[data-radix-popper-content-wrapper]',
      '[data-table-grid-dialog]',
    ],
  });

  // Stop editing if the field turns read-only under us.
  useEffect(() => {
    if (!editable && editing) setEditing(false);
  }, [editable, editing]);

  // Hold the single-active lock while editing (closing any prior holder); release
  // and persist via `onEditEnd` when editing stops.
  useEffect(() => {
    if (!active) return;
    if (
      activeSharedMarkdownEditor &&
      activeSharedMarkdownEditor.id !== editorId
    ) {
      activeSharedMarkdownEditor.close();
    }
    activeSharedMarkdownEditor = {
      id: editorId,
      close: () => setEditing(false),
    };
    return () => {
      if (activeSharedMarkdownEditor?.id === editorId) {
        activeSharedMarkdownEditor = null;
      }
      onEditEndRef.current?.();
    };
  }, [active, editorId]);

  const startEditing = (focusOffset: { x: number; y: number } | null) => {
    focusOffsetRef.current = focusOffset;
    // Bump so the pooled editor re-seeds from `value` on each open.
    versionRef.current += 1;
    setEditing(true);
  };

  return (
    <div
      ref={wrapperRef}
      className={className ?? 'flex min-h-0 flex-1 flex-col'}
    >
      {useSingleton ? (
        <div
          ref={hostRef}
          aria-label={ariaLabel}
          className="flex min-h-0 flex-1 flex-col"
        />
      ) : active ? (
        <MarkdownEditor
          id={editorId}
          key={`${versionRef.current}:${syncVersion ?? ''}`}
          initialContent={value}
          ariaLabel={ariaLabel}
          placeholder={placeholder}
          featureSet={featureSet}
          extensionMode="full"
          autofocus
          findScrollableParent={false}
          className="min-h-0 flex-1 lb-editor-reset px-5 pb-2 pt-3"
          onUpdate={(markdown) => onChange(markdown)}
          onAskSelection={onAskSelection}
        />
      ) : (
        <div
          role={editable ? 'button' : undefined}
          tabIndex={editable ? 0 : undefined}
          aria-label={
            editable && ariaLabel
              ? `Edit ${ariaLabel}`
              : (ariaLabel ?? undefined)
          }
          className={classList(
            'flex min-h-0 flex-1 flex-col outline-none',
            editable && 'cursor-text focus-ring'
          )}
          onPointerDown={
            editable
              ? (event) => {
                  if (event.pointerType === 'mouse' && event.button !== 0)
                    return;
                  event.preventDefault();
                  const contentEl = event.currentTarget.querySelector(
                    '.copyable-block-preview-content'
                  );
                  const rect =
                    contentEl?.getBoundingClientRect() ??
                    event.currentTarget.getBoundingClientRect();
                  startEditing({
                    x: event.clientX - rect.left,
                    y: event.clientY - rect.top,
                  });
                }
              : undefined
          }
          onKeyDown={
            editable
              ? (event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  startEditing(null);
                }
              : undefined
          }
        >
          {value.trim() ? (
            <EditorPreview
              content={value}
              editable={editable}
              className="min-h-0 flex-1 lb-editor-reset px-5 pb-2 pt-3"
              onUpdate={onPreviewChange}
            />
          ) : (
            <div className="min-h-0 flex-1 px-5 pb-2 pt-3 text-sm text-foreground-muted">
              {placeholder ?? ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
